import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Download,
  FileText,
  History,
  Loader2,
  Play,
  Plus,
  Trash2,
  XCircle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";
import { MultiSelectPopover, MultiSelectItem } from "../components/MultiSelectPopover";
import { useAuth } from "../components/features/AuthContext";
import { useToast } from "../hooks/useToast";

const CRYPTO_CATEGORY = "crypto";
const JOBS_STORAGE_KEY = "crypto_backtest_job_ids";

const INTERVALS = ["5s", "15s", "30s", "1m", "3m", "5m", "15m", "30m", "1h", "4h", "1d"];
const YEARS = [1, 2, 3, 4, 5];

export interface BacktestResult {
  symbol: string;
  strategy: string;
  report_summary?: Record<string, any> | null;
  pdf_path?: string | null;
  error?: string;
}

export interface BacktestJob {
  _id: string;
  userId: string;
  filterIds: string[];
  bucketIds: string[];
  status: "pending" | "running" | "completed" | "failed";
  progress: number;
  message: string;
  result?: BacktestResult[] | null;
  pdf_path?: string | null;
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
}

interface BacktestFiltersRow {
  id: string;
  function_name: string;
}

interface BucketRow {
  _id: string;
  name: string;
}

const slTpPairSchema = z.object({
  sl: z.coerce.number().min(0, "Required"),
  tp: z.coerce.number().min(0, "Required"),
});

const backtestSchema = z.object({
  filterIds: z.array(z.string()).min(1, "Select at least one filter"),
  bucketIds: z.array(z.string()).min(1, "Select at least one bucket"),
  years: z.coerce.number().int().min(1).max(5),
  interval: z.string().min(1),
  holdMinutes: z
    .string()
    .min(1, "Required")
    .refine(
      (value) =>
        value
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)
          .every((part) => Number.isInteger(Number(part)) && Number(part) > 0),
      "Comma separated whole minutes, e.g. 240, 480, 720"
    ),
  slTpPairs: z.array(slTpPairSchema).min(1, "Add at least one SL/TP pair"),
});

export type BacktestFormValues = z.infer<typeof backtestSchema>;

const defaultValues: BacktestFormValues = {
  filterIds: [],
  bucketIds: [],
  years: 1,
  interval: "1m",
  holdMinutes: "240, 480, 720, 1440",
  slTpPairs: [
    { sl: 0.01, tp: 0.005 },
    { sl: 0.02, tp: 0.01 },
  ],
};

function readStoredJobIds(): string[] {
  try {
    const raw = localStorage.getItem(JOBS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export default function Backtesting() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [jobIds, setJobIds] = useState<string[]>(readStoredJobIds);

  useEffect(() => {
    localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobIds));
  }, [jobIds]);

  const form = useForm<BacktestFormValues>({
    resolver: zodResolver(backtestSchema),
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "slTpPairs",
  });

  const { data: filters, isLoading: filtersLoading } = useQuery<BacktestFiltersRow[]>({
    queryKey: ["api/filters", CRYPTO_CATEGORY, "backtest"],
    queryFn: async () => {
      const params = new URLSearchParams({ category: CRYPTO_CATEGORY, code: "0" });
      const res = await apiRequest<ApiResponse<BacktestFiltersRow[]>>(
        "GET",
        `api/filters?${params.toString()}`
      );
      return res.data ?? [];
    },
  });

  const { data: buckets, isLoading: bucketsLoading } = useQuery<BucketRow[]>({
    queryKey: ["api/buckets", user?.userId, user?.role, CRYPTO_CATEGORY],
    queryFn: async () => {
      if (!user?.userId || !user?.role) return [];
      const params = new URLSearchParams({
        userId: user.userId,
        role: user.role,
        category: CRYPTO_CATEGORY,
      });
      const res = await apiRequest<ApiResponse<BucketRow[]>>(
        "GET",
        `api/buckets?${params.toString()}`
      );
      return res.data ?? [];
    },
    enabled: !!user?.userId && !!user?.role,
  });

  const filterItems: MultiSelectItem[] = useMemo(
    () =>
      (filters ?? [])
        .filter((filter) => filter.id)
        .map((filter) => ({ id: filter.id, label: filter.function_name })),
    [filters]
  );

  const bucketItems: MultiSelectItem[] = useMemo(
    () => (buckets ?? []).map((bucket) => ({ id: bucket._id, label: bucket.name })),
    [buckets]
  );

  const startBacktest = useMutation({
    mutationFn: async (values: BacktestFormValues) => {
      const payload = {
        userId: user?.userId,
        filterIds: values.filterIds,
        bucketIds: values.bucketIds,
        years: values.years,
        interval: values.interval,
        max_hold_minutes: values.holdMinutes
          .split(",")
          .map((part) => Number(part.trim()))
          .filter((part) => Number.isInteger(part) && part > 0),
        sl_tp_pairs: values.slTpPairs.map((pair) => [pair.sl, pair.tp]),
      };
      return apiRequest<ApiResponse<{ job_id: string; status: string; progress: number }>>(
        "POST",
        "api/backtests",
        payload,
        undefined,
        "crypto"
      );
    },
    onSuccess: (res) => {
      const jobId = res?.data?.job_id;
      if (jobId) {
        setJobIds((current) => (current.includes(jobId) ? current : [jobId, ...current]));
      }
      toast({ title: "Backtest queued", description: "The job is running in the background." });
      queryClient.invalidateQueries({ queryKey: ["api/backtests"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to start backtest",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const removeJob = (jobId: string) => setJobIds((current) => current.filter((id) => id !== jobId));

  const onSubmit = (values: BacktestFormValues) => startBacktest.mutate(values);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5" /> Start New Backtest
          </CardTitle>
          <CardDescription>
            Runs asynchronously on the crypto backend: pick filters and buckets, then poll the job
            for progress and download the PDF report when it finishes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Filters</Label>
                  <FormField
                    control={form.control}
                    name="filterIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <MultiSelectPopover
                            items={filterItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              filtersLoading ? "Loading filters..." : "Select filters"
                            }
                            accent="blue"
                            disabled={filtersLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Buckets</Label>
                  <FormField
                    control={form.control}
                    name="bucketIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <MultiSelectPopover
                            items={bucketItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={bucketsLoading ? "Loading buckets..." : "Select buckets"}
                            accent="purple"
                            disabled={bucketsLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Years of history</Label>
                  <FormField
                    control={form.control}
                    name="years"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={String(field.value)}
                          onValueChange={(value) => field.onChange(Number(value))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Years" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {YEARS.map((year) => (
                              <SelectItem key={year} value={String(year)}>
                                {year} year{year > 1 ? "s" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Candle interval</Label>
                  <FormField
                    control={form.control}
                    name="interval"
                    render={({ field }) => (
                      <FormItem>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Interval" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {INTERVALS.map((interval) => (
                              <SelectItem key={interval} value={interval}>
                                {interval}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-semibold text-gray-700">
                    Max hold times (minutes, comma separated)
                  </Label>
                  <FormField
                    control={form.control}
                    name="holdMinutes"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder="240, 480, 720, 1440" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-700">
                    Stop loss / take profit pairs
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ sl: 0.02, tp: 0.01 })}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add pair
                  </Button>
                </div>

                <div className="space-y-2">
                  {fields.map((row, index) => (
                    <div key={row.id} className="flex items-center gap-3">
                      <FormField
                        control={form.control}
                        name={`slTpPairs.${index}.sl`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input type="number" step="0.001" placeholder="Stop loss" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`slTpPairs.${index}.tp`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input type="number" step="0.001" placeholder="Take profit" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={fields.length === 1}
                        onClick={() => remove(index)}
                        aria-label="Remove pair"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Each pair is tested against every max hold time listed above.
                </p>
              </div>

              <Button type="submit" disabled={startBacktest.isPending}>
                {startBacktest.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" /> Start Backtest
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Backtest Jobs
          </CardTitle>
          <CardDescription>
            Jobs started in this browser are kept here so you can keep polling their progress.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {jobIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No backtest jobs yet. Start one above to see live progress here.
            </p>
          ) : (
            jobIds.map((jobId) => (
              <BacktestJobCard key={jobId} jobId={jobId} onRemove={() => removeJob(jobId)} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BacktestJobCard({ jobId, onRemove }: { jobId: string; onRemove: () => void }) {
  const { toast } = useToast();

  const { data: response, error, isLoading } = useQuery<ApiResponse<BacktestJob>>({
    queryKey: ["api/backtests", jobId],
    queryFn: async () =>
      apiRequest<ApiResponse<BacktestJob>>(
        "GET",
        `api/backtests/${jobId}`,
        undefined,
        undefined,
        "crypto"
      ),
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      return status === "pending" || status === "running" ? 2000 : false;
    },
    retry: false,
  });

  const job = response?.data;
  const status = job?.status ?? "pending";
  const isRunning = status === "pending" || status === "running";

  const downloadReport = useMutation({
    mutationFn: async () => {
      const blob = await apiRequest<Blob>(
        "GET",
        `api/backtests/${jobId}/download`,
        undefined,
        undefined,
        "crypto",
        undefined,
        "blob"
      );
      return blob;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const fileName = job?.pdf_path?.split("/").pop() || `backtest_report_${jobId}.pdf`;
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    },
    onError: (err: any) => {
      toast({
        title: "Download failed",
        description: err?.message || "Could not download the report",
        variant: "destructive",
      });
    },
  });

  const statusBadge = () => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" /> Failed
          </Badge>
        );
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Activity className="w-3 h-3 mr-1" /> Pending
          </Badge>
        );
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {statusBadge()}
          <span className="text-sm font-mono text-gray-500">{jobId}</span>
        </div>
        <div className="flex items-center gap-2">
          {status === "completed" && (
            <Button
              size="sm"
              variant="outline"
              disabled={downloadReport.isPending}
              onClick={() => downloadReport.mutate()}
            >
              {downloadReport.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              Download PDF
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onRemove} aria-label="Remove job">
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading job status...</p>}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          {(error as Error).message}
        </div>
      )}

      {job && (
        <>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{job.message || "Waiting for the worker..."}</span>
              <span>{Math.round(job.progress ?? 0)}%</span>
            </div>
            <Progress value={job.progress ?? 0} />
          </div>

          {status === "failed" && (
            <p className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {job.message}
            </p>
          )}

          {!!job.result?.length && (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Symbol</th>
                    <th className="px-3 py-2 font-medium">Strategy</th>
                    <th className="px-3 py-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {job.result.map((item, index) => (
                    <tr key={`${item.symbol}-${item.strategy}-${index}`} className="border-t">
                      <td className="px-3 py-2 font-medium">{item.symbol}</td>
                      <td className="px-3 py-2">{item.strategy}</td>
                      <td className="px-3 py-2">
                        {item.error ? (
                          <span className="text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {item.error}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-600">
                            <FileText className="w-3 h-3" />
                            {item.pdf_path ? "Report ready" : "No PDF generated"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {job.created_at && (
            <p className="text-xs text-muted-foreground">
              Started {new Date(job.created_at).toLocaleString()}
              {job.completed_at ? ` · Finished ${new Date(job.completed_at).toLocaleString()}` : ""}
            </p>
          )}
        </>
      )}
    </div>
  );
}
